import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import Reveal from "./Reveal";
import { useT } from "@/hooks/useT";
import {
  ArrowRight,
  Calendar,
  Users,
  Sparkles,
  BedDouble,
  CheckCircle2,
  ShieldCheck,
  Zap,
  CreditCard,
} from "lucide-react";

/**
 * Booking Engine showcase — premium two-column layout.
 * Left: copy + CTA. Right: hotel website mockup with floating widgets.
 */
export default function BookingEngine() {
  const t = useT();
  return (
    <section
      className="relative overflow-hidden bg-[#F8FAFC] py-20 md:py-28"
      data-testid="booking-engine-section"
    >
      {/* Soft background glows */}
      <div
        className="pointer-events-none absolute -top-32 right-[-10%] h-[640px] w-[640px] rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.08),transparent_70%)] blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-[-15%] left-[-10%] h-[520px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(56,109,212,0.06),transparent_70%)] blur-3xl"
        aria-hidden
      />

      <div className="container-akm relative">
        <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
          {/* ─── LEFT — Copy ─── */}
          <Reveal variant="left" data-testid="booking-engine-copy">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#EA580C]">
              <Sparkles size={12} className="text-[#FF7A00]" />
              Booking Engine
            </span>
            <h2
              className="mt-4 font-display font-extrabold tracking-[-0.035em] text-[#0F172A]"
              style={{
                fontSize: "clamp(1.95rem, 1.1rem + 1.8vw, 3rem)",
                lineHeight: 1.08,
              }}
              data-testid="booking-engine-heading"
            >
              {t("Merrni rezervime direkte", "Take direct bookings")}{" "}
              <span className="text-[#F26322]">{t("nga website-i juaj", "from your own website")}</span>
            </h2>
            <p className="mt-5 max-w-xl text-[15.5px] leading-[1.75] text-[#475569]">
              {t(
                "Booking engine i fuqishëm, i shpejtë dhe i lehtë për t'u përdorur. Rezervimet kalojnë direkt në sistem dhe sinkronizohen automatikisht me dhomat dhe kalendarin e hotelit.",
                "A fast, powerful booking engine that's easy to use. Reservations flow straight into the system and sync automatically with your rooms and calendar."
              )}
            </p>

            {/* Mini bullets */}
            <ul className="mt-7 space-y-2.5">
              {[
                { icon: ShieldCheck, text: t("Pa komisione për rezervime direkte.", "Zero commissions on direct bookings.") },
                { icon: Zap,         text: t("Sinkronizim automatik me PMS dhe kanalet.", "Automatic sync with PMS and every channel.") },
                { icon: CreditCard,  text: t("Pagesa të sigurta me kartë në kohë reale.", "Secure real-time card payments.") },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-2.5">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-gradient-to-br from-[#FFF1E5] to-[#FFE0CC] text-[#EA580C] ring-1 ring-[#FFD9B5]/60">
                    <Icon size={13} strokeWidth={2.2} />
                  </span>
                  <span className="text-[13.5px] leading-[1.55] text-[#0F172A]">
                    {text}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <Link
                to="/platforma/booking-engine"
                data-testid="booking-engine-cta"
                className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-b from-[#FF9A3D] to-[#EA580C] px-7 py-3.5 text-[13px] font-semibold tracking-tight text-white shadow-[0_14px_36px_-12px_rgba(234,88,12,0.55),inset_0_1px_0_rgba(255,255,255,0.25)] transition-all hover:shadow-[0_18px_44px_-12px_rgba(234,88,12,0.75)]"
              >
                {t("Zbulo Booking Engine", "Discover Booking Engine")}
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
            </div>
          </Reveal>

          {/* ─── RIGHT — Website + floating widgets ─── */}
          <Reveal variant="right" delay={180}>
            <BookingMockup />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   BOOKING MOCKUP — modern hotel website with floating widgets
   ───────────────────────────────────────────────────────────────────── */
function BookingMockup() {
  const t = useT();
  return (
    <div className="relative" data-testid="booking-mockup">
      <div className="relative mx-auto aspect-[10/11] w-full max-w-[640px]">
        {/* Soft halo behind the device */}
        <div
          className="absolute inset-[6%] -z-10 rounded-[40px] bg-[radial-gradient(closest-side,rgba(255,138,61,0.18),transparent_70%)] blur-2xl"
          aria-hidden
        />

        {/* ─── Browser window (hotel website) ─── */}
        <div className="absolute inset-x-0 top-0 w-full overflow-hidden rounded-2xl bg-white shadow-[0_50px_120px_-40px_rgba(11,18,32,0.4),0_0_0_1px_rgba(15,23,42,0.05)] ring-1 ring-black/[0.04]">
          {/* Window chrome */}
          <div className="flex items-center gap-1.5 border-b border-[#F1F5F9] bg-[#FAFBFC] px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
            <span className="mx-auto -translate-x-3 rounded-md bg-white px-3 py-0.5 text-[10px] text-[#94A3B8] ring-1 ring-[#E2E8F0]">
              hotelluksor.al
            </span>
          </div>

          {/* Hotel hero (dark navigation + image) */}
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#0F172A]">
            {/* Background image */}
            <img
              src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=1600&q=80"
              alt="Hotel hero"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover opacity-90"
            />
            <div
              className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/70 via-[#0F172A]/20 to-[#0F172A]/85"
              aria-hidden
            />

            {/* Top nav */}
            <div className="relative flex items-center justify-between px-5 pt-4 text-white">
              <span className="font-display text-[14px] font-extrabold tracking-tight">
                Hotel Luksor
              </span>
              <div className="hidden gap-4 text-[10.5px] font-medium text-white/85 sm:flex">
                <span>Dhomat</span>
                <span>Restorant</span>
                <span>Eventet</span>
                <span>Kontakt</span>
              </div>
              <span className="rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold text-white ring-1 ring-white/20">
                Rezervo
              </span>
            </div>

            {/* Hero headline */}
            <div className="absolute inset-x-0 bottom-[26%] px-5 text-white">
              <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-white/70">
                {t("Rezidenca pranë detit", "Seaside residence")}
              </div>
              <div className="mt-1 font-display text-[20px] font-extrabold leading-tight tracking-tight">
                {t("Pushime që mbahen mend.", "Holidays worth remembering.")}
              </div>
            </div>

            {/* Booking bar */}
            <div className="absolute inset-x-3 bottom-3 rounded-xl bg-white p-2 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.55)] ring-1 ring-black/5">
              <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2 text-[10px]">
                <BookingField label="Check-in"  value={t("12 Qer 2026","12 Jun 2026")} />
                <BookingField label="Check-out" value={t("15 Qer 2026","15 Jun 2026")} />
                <BookingField label={t("Klientë","Guests")}   value={t("2 të rritur","2 adults")} />
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md bg-gradient-to-b from-[#FF9A3D] to-[#EA580C] px-3 py-2 text-[10px] font-semibold text-white shadow-[0_8px_18px_-8px_rgba(234,88,12,0.65)]"
                >
                  {t("Rezervo","Book")}
                  <ArrowRight size={10} />
                </button>
              </div>
            </div>
          </div>

          {/* Body — room cards under */}
          <div className="grid grid-cols-3 gap-2 p-3">
            {[
              { name: t("Suita 304","Suite 304"),   price: "€214" },
              { name: t("Standard","Standard"),     price: "€128" },
              { name: t("Deluxe","Deluxe"),         price: "€168" },
            ].map((r) => (
              <div
                key={r.name}
                className="rounded-lg border border-[#F1F5F9] bg-white p-2"
              >
                <div className="aspect-[16/10] w-full rounded bg-gradient-to-br from-[#E2E8F0] to-[#CBD5E1]" />
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-[9.5px] font-semibold text-[#0F172A]">
                    {r.name}
                  </span>
                  <span className="text-[9.5px] font-bold text-[#EA580C]">
                    {r.price}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Floating: Reservation confirmation widget (top-right) ─── */}
        <div
          className="absolute right-[-3%] top-[12%] w-[44%] max-w-[230px] rounded-2xl border border-white/80 bg-white/95 p-3 shadow-[0_24px_60px_-20px_rgba(11,18,32,0.28)] ring-1 ring-black/[0.04] backdrop-blur-xl"
          data-testid="booking-card-confirm"
        >
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-[#FF9A3D] to-[#EA580C] shadow-[0_6px_14px_-4px_rgba(234,88,12,0.55)]">
              <BedDouble size={14} className="text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-semibold text-[#0F172A]">
                {t("Rezervim direkt", "Direct booking")}
              </div>
              <div className="text-[9.5px] text-emerald-600">
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 size={9} /> {t("Konfirmuar", "Confirmed")}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-2.5 space-y-1 rounded-lg bg-[#F8FAFC] p-2">
            <Row k={t("Dhoma","Room")} v={t("Suita 304","Suite 304")} />
            <Row k={t("Net","Nights")}   v="3" />
            <Row k="Total" v="€642" bold />
          </div>
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9.5px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
            <Zap size={9} /> {t("0% komision", "0% commission")}
          </div>
        </div>

        {/* ─── Floating: Availability mini calendar (left-middle) ─── */}
        <div
          className="absolute left-[-3%] top-[44%] w-[42%] max-w-[230px] rounded-2xl border border-white/80 bg-white/95 p-3 shadow-[0_24px_60px_-20px_rgba(11,18,32,0.28)] ring-1 ring-black/[0.04] backdrop-blur-xl"
          data-testid="booking-card-calendar"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Calendar size={12} className="text-[#EA580C]" />
              <span className="text-[10.5px] font-semibold text-[#0F172A]">
                {t("Qershor 2026", "June 2026")}
              </span>
            </div>
            <Users size={11} className="text-[#94A3B8]" />
          </div>
          <div className="mt-2 grid grid-cols-7 gap-0.5 text-center text-[8px] font-medium text-[#94A3B8]">
            {["H","M","M","E","P","Sh","D"].map((d, i) => <span key={i}>{d}</span>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-0.5">
            {Array.from({ length: 21 }).map((_, i) => {
              const limited = [4, 5, 11, 12].includes(i);
              const blocked = [9, 16, 17].includes(i);
              const today   = i === 7;
              return (
                <span
                  key={i}
                  className={`grid h-5 place-items-center rounded text-[8px] font-semibold ${
                    today
                      ? "bg-[#0F172A] text-white"
                      : blocked
                      ? "bg-[#F1F5F9] text-[#CBD5E1]"
                      : limited
                      ? "bg-[#FFF1E5] text-[#EA580C] ring-1 ring-[#FFD9B5]"
                      : "bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {i + 1}
                </span>
              );
            })}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1 text-[8.5px]">
            <span className="inline-flex items-center gap-1 text-[#0F172A]">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t("Lirë","Free")}
            </span>
            <span className="inline-flex items-center gap-1 text-[#0F172A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" /> {t("Pak","Few")}
            </span>
            <span className="inline-flex items-center gap-1 text-[#0F172A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#CBD5E1]" /> {t("Zëna","Booked")}
            </span>
          </div>
        </div>

        {/* ─── Sync chip (bottom-center) ─── */}
        <div
          className="absolute bottom-[2%] left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/80 bg-white/95 px-3 py-1.5 shadow-[0_18px_36px_-12px_rgba(11,18,32,0.28)] ring-1 ring-black/[0.04] backdrop-blur-xl"
          data-testid="booking-card-sync"
        >
          <span className="relative grid h-5 w-5 place-items-center rounded-full bg-emerald-50 text-emerald-600">
            <Zap size={10} />
            <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500 ring-2 ring-white" />
          </span>
          <div>
            <div className="text-[9.5px] font-semibold leading-tight text-[#0F172A]">
              {t("Sinkronizuar me PMS", "Synced with PMS")}
            </div>
            <div className="text-[8.5px] leading-tight text-[#94A3B8]">
              {t("Rezervimi në sistem · 2s", "Booking in the system · 2s")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Small helpers ─── */
function BookingField({ label, value }) {
  return (
    <div className="rounded-md bg-[#F8FAFC] px-2 py-1.5 ring-1 ring-[#F1F5F9]">
      <div className="text-[8px] font-semibold uppercase tracking-[0.14em] text-[#94A3B8]">
        {label}
      </div>
      <div className="text-[10px] font-semibold text-[#0F172A]">{value}</div>
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className="flex items-center justify-between text-[10px]">
      <span className="text-[#64748B]">{k}</span>
      <span className={`text-[#0F172A] ${bold ? "font-bold" : "font-semibold"}`}>
        {v}
      </span>
    </div>
  );
}
